import React, { useState, useEffect } from 'react';
import supabase from '../supabaseClient';
import bcrypt from 'bcryptjs';
import './signstyles.css'; // Import the CSS file for sign-in styles
import './blogstyle.css'; // Import the CSS file for blog creation styles

const GhostSignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [blogTitle, setBlogTitle] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();

    // Fetch the admin data from the 'admin' table by email
    const { data: adminData, error } = await supabase
      .from('admin')
      .select('*')
      .eq('email', email)
      .single();

    if (error || !adminData) {
      alert('No admin user found with this email');
      return;
    }

    // Compare the entered password with the hashed password in the database
    const isValidPassword = await bcrypt.compare(password, adminData.password);
    if (!isValidPassword) {
      alert('Incorrect password');
      return;
    }

    // If valid, store session and show blog creation form
    localStorage.setItem('adminSession', JSON.stringify(adminData));
    setIsAuthenticated(true);
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile && (uploadedFile.type === 'application/msword' || uploadedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      setFile(uploadedFile);
    } else {
      alert('Please upload a valid Word file (.doc or .docx).');
    }
  };

  const handleImageChange = (e) => {
    const uploadedImage = e.target.files[0];
    if (uploadedImage && (uploadedImage.type.startsWith('image/'))) {
      setImage(uploadedImage);
    } else {
      alert('Please upload a valid image file.');
    }
  };

  const handleBlogCreation = async (e) => {
    e.preventDefault();

    // Handle file upload to Supabase Storage
    let fileUrl = '';
    if (file) {
      const fileName = `${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('blog-files')
        .upload(fileName, file);

      if (uploadError) {
        alert('Error uploading file: ' + uploadError.message);
        return;
      }

      fileUrl = `${supabase.storage
        .from('blog-files')
        .getPublicUrl(fileName).publicURL}`;
    }

    // Handle image upload to Supabase Storage
    let imageUrl = '';
    if (image) {
      const imageName = `${Date.now()}_${image.name}`;
      const { error: imageUploadError } = await supabase.storage
        .from('blog-images')
        .upload(imageName, image);

      if (imageUploadError) {
        alert('Error uploading image: ' + imageUploadError.message);
        return;
      }

      imageUrl = `${supabase.storage
        .from('blog-images')
        .getPublicUrl(imageName).publicURL}`;
    }

    // Insert the blog post into Supabase database
    const { error } = await supabase
      .from('blogs')
      .insert([{ title: blogTitle, content: blogContent, file_url: fileUrl, image_url: imageUrl }]);

    if (error) {
      console.error('Error creating blog:', error);
      alert('Error creating blog: ' + error.message);
    } else {
      alert('Blog created successfully!');
      setBlogTitle('');
      setBlogContent('');
      setFile(null);
      setImage(null);
      fetchBlogs(); // Refresh the blog list
    }
  };

  const fetchBlogs = async () => {
    const { data: blogData, error } = await supabase
      .from('blogs')
      .select('*');

    if (error) {
      console.error('Error fetching blogs:', error);
      return;
    }

    setBlogs(blogData);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchBlogs();
    }
  }, [isAuthenticated]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleEditBlog = (blogId) => {
    // Implement logic to handle blog editing
    console.log('Edit blog with ID:', blogId);
  };

  return (
    <div className="sign-in-container">
      {isAuthenticated && (
        <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <button className="sidebar-close" onClick={toggleSidebar}>X</button>
          <h2>Blogs</h2>
          <ul>
            {blogs.map(blog => (
              <li key={blog.id}>
                <span>{blog.title}</span>
                <button onClick={() => handleEditBlog(blog.id)}>Edit</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {isAuthenticated && (
        <button className="hamburger" onClick={toggleSidebar}>
          ☰
        </button>
      )}
      {!isAuthenticated ? (
        <>
          <h1 className="sign-in-heading">Admin Sign In</h1>
          <form className="sign-in-form" onSubmit={handleSignIn}>
            <div>
              <label>Email</label>
              <input
                type="email"
                className="sign-in-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Password</label>
              <input
                type="password"
                className="sign-in-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="sign-in-button">Sign In</button>
          </form>
        </>
      ) : (
        <div className="blog-creation-container">
          <h1 className="sign-in-heading">Admin Blog Creation</h1>
          <form className="blog-creation-form" onSubmit={handleBlogCreation}>
            <div>
              <label>Title</label>
              <input
                type="text"
                className="blog-input"
                value={blogTitle}
                onChange={(e) => setBlogTitle(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Content</label>
              <textarea
                className="blog-textarea"
                value={blogContent}
                onChange={(e) => setBlogContent(e.target.value)}
                required
              ></textarea>
            </div>
            <div>
              <label>Upload File (Word document only)</label>
              <input
                type="file"
                className="blog-file-input"
                accept=".doc,.docx"
                onChange={handleFileChange}
              />
            </div>
            <div>
              <label>Upload Image</label>
              <input
                type="file"
                className="blog-image-input"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>
            <button type="submit" className="blog-button">Create Blog</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default GhostSignIn;
